import React, { CSSProperties, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { HOVER_CARD_LEFT_SKEW } from './onhoverhierarchyinfo';

interface PortalWrapperProps {
  hoverDivPosition: DOMRect | undefined;
  iconPosition: DOMRect;
  showPortal: boolean;
  onOutsideClick: () => void;
}

const mapStyleToString = (style: CSSProperties) =>
  Object.entries(style).reduce(
    (acc: string, [key, value]) => acc.concat(`${key}:${value}px;`),
    '',
  );

const getDerivedStyleFromPosition = (
  iconPosition: DOMRect,
  hoverDivPosition?: DOMRect,
): string => {
  const style: CSSProperties = {};
  const middleOfScreenY = window.innerHeight / 2;

  // If the card should be tilted upwards or downwards from help icon
  const cardUnderIcon = iconPosition.bottom < middleOfScreenY;

  if (cardUnderIcon || !hoverDivPosition) {
    style['top'] = iconPosition.y + iconPosition.height + 10 + window.scrollY;
  } else {
    style['top'] =
      iconPosition.y - hoverDivPosition.height - 10 + window.scrollY;
  }

  // By default have the card only slightly skewed
  style['left'] = iconPosition.x - HOVER_CARD_LEFT_SKEW;

  const pxStrings = mapStyleToString(style);
  return `${pxStrings}position:absolute;z-index:9997`;
};

const PortalWrapper: React.FC<React.PropsWithChildren<PortalWrapperProps>> = ({
  children,
  hoverDivPosition,
  iconPosition,
  showPortal,
  onOutsideClick,
}) => {
  const portalRef = useRef<HTMLDivElement>(document.createElement('div'));

  useEffect(() => {
    const element = portalRef.current;

    const removePortal = () => {
      document.body.contains(element) && document.body.removeChild(element);
    };

    if (element && showPortal) {
      document.body.appendChild(element);

      element.setAttribute(
        'style',
        `${getDerivedStyleFromPosition(iconPosition, hoverDivPosition)}`,
      );
    } else {
      removePortal();
    }

    const clickedOutside = (
      x: number,
      y: number,
      elementPosition: DOMRect,
    ): boolean => {
      const {
        x: elX,
        y: elY,
        width: elWidth,
        height: elHeight,
      } = elementPosition;
      const [elXEnd, elYEnd] = [elX + elWidth, elY + elHeight];

      return x < elX || x > elXEnd || y < elY || y > elYEnd;
    };

    const onClick = (event: MouseEvent) => {
      if (
        element &&
        clickedOutside(
          event.clientX,
          event.clientY,
          portalRef.current.getBoundingClientRect(),
        )
      ) {
        onOutsideClick();
        removePortal();
      }
    };

    document.addEventListener('mousedown', onClick);
    document.addEventListener('mouseup', onClick);

    return () => {
      removePortal();
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('mouseup', onClick);
    };
  }, [showPortal, portalRef, hoverDivPosition, iconPosition, onOutsideClick]);

  return portalRef.current ? createPortal(children, portalRef.current) : <></>;
};

export default PortalWrapper;
